import type { Place } from "@instead/shared";

/**
 * Transient rest context (§5.10): where you are and what's available, held
 * client-side only (sessionStorage, 4 h TTL) and passed explicitly to shuffle.
 * The one-tap location check runs Geolocation ONCE, matches saved Places
 * on-device (haversine vs stored radius), keeps only the matched place id —
 * raw coordinates are discarded immediately and never leave the device.
 */
export interface RestContext {
  placeId?: string;
  placeName?: string;
  metNeedIds: string[];
  expiresAt: number;
}

const KEY = "instead.context";
const TTL_MS = 4 * 3600_000;

export const restContext = {
  get(): RestContext | null {
    try {
      const raw = sessionStorage.getItem(KEY);
      if (!raw) return null;
      const ctx = JSON.parse(raw) as RestContext;
      if (ctx.expiresAt < Date.now()) {
        sessionStorage.removeItem(KEY);
        return null;
      }
      return ctx;
    } catch {
      return null;
    }
  },
  set(partial: Omit<RestContext, "expiresAt">): RestContext {
    const ctx: RestContext = { ...partial, expiresAt: Date.now() + TTL_MS };
    sessionStorage.setItem(KEY, JSON.stringify(ctx));
    return ctx;
  },
  clear() {
    sessionStorage.removeItem(KEY);
  },
};

export function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371_000;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(lat2 - lat1);
  const dLng = rad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** One-tap location check. Resolves to the matched place (or null); coordinates never persist. */
export function locateOnce(places: Place[]): Promise<Place | null> {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      reject(new Error("no-geolocation"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const match = places.find(
          (p) =>
            p.lat != null && p.lng != null &&
            haversineM(latitude, longitude, p.lat, p.lng) <= (p.radiusM ?? 250)
        );
        resolve(match ?? null); // pos goes out of scope here — nothing stored
      },
      (err) => reject(new Error(err.message)),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 }
    );
  });
}

/** Capture coordinates once for SAVING a place the user explicitly asked to save. */
export function captureOnce(): Promise<{ lat: number; lng: number }> {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      reject(new Error("no-geolocation"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(new Error(err.message)),
      { enableHighAccuracy: true, timeout: 10_000 }
    );
  });
}
